import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { DayPicker, type DateRange, type DayButtonProps } from 'react-day-picker';

import './dateRangeCalendar.css';

import {
  calendarDateToIso,
  commitCalendarRange,
  isoToCalendarDate,
  type DateRangeCalendarProps,
} from './dateRangeCalendarValue';

function dayTestId(date: Date): string {
  return `date-range-day-${format(date, 'yyyy-MM-dd')}`;
}

function FlareDayButton({ day, modifiers: _modifiers, ...buttonProps }: DayButtonProps) {
  const id = dayTestId(day.date);
  // RNTL's getByTestId reads the testID prop. The DOM attribute is data-testid.
  const testProps = { testID: id } as React.ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button
      {...buttonProps}
      type="button"
      data-testid={id}
      {...testProps}
    />
  );
}

/**
 * One-month range calendar for the dashboard date popover.
 * Props only: the page owns the ISO from/to.
 */
export function DateRangeCalendar({
  from,
  to,
  onFromChange,
  onToChange,
  disabled = false,
}: DateRangeCalendarProps) {
  const committed = useMemo(() => {
    const fromDate = isoToCalendarDate(from);
    const toDate = isoToCalendarDate(to);
    if (!fromDate) return undefined;
    return { from: fromDate, to: toDate };
  }, [from, to]);
  const [draft, setDraft] = useState<DateRange | undefined>(undefined);
  const selected = draft ?? committed;

  const onSelect = (range: DateRange | undefined) => {
    const next = commitCalendarRange(range);
    if (!next) {
      setDraft(range);
      return;
    }
    setDraft(undefined);
    onFromChange(next.from);
    onToChange(next.to);
  };

  return (
    <div className="flare-day-picker" data-testid="date-range-calendar">
      <DayPicker
        mode="range"
        weekStartsOn={1}
        navLayout="around"
        showOutsideDays
        fixedWeeks
        resetOnSelect
        animate={false}
        disabled={disabled}
        selected={selected}
        onSelect={onSelect}
        defaultMonth={committed?.to ?? committed?.from}
        components={{ DayButton: FlareDayButton }}
      />
    </div>
  );
}

export { calendarDateToIso, isoToCalendarDate };
